import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { QRCodeCanvas } from 'qrcode.react';
import { createTranslator, useLanguage } from '../i18n/LanguageContext';
import { leagueTranslations } from '../i18n/translations';

const FUNCTIONS_BASE_URL = 'https://us-central1-mendyx-adafe.cloudfunctions.net';

const eloCategories = {
  P50: 600,
  P100: 700,
  P150: 800,
  P200: 900,
  P300: 1200,
  P400: 1600,
  P500: 2000,
};

const functionUrl = (functionName, params = {}) => {
  const url = new URL(`${FUNCTIONS_BASE_URL}/${functionName}`);

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      url.searchParams.set(key, value);
    }
  });

  return url.toString();
};

const postFunctionJson = async (functionName, body) => {
  const response = await fetch(functionUrl(functionName), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    const error = new Error(data.error || `Erreur function ${functionName}: ${response.status}`);
    error.status = response.status;
    error.data = data;
    throw error;
  }

  return data;
};

const duplicateMessage = (reason, t) => (reason === 'email' ? t('duplicateEmail') : t('duplicateTeam'));

const LeagueRegistration = () => {
  const { language } = useLanguage();
  const t = createTranslator(language, leagueTranslations);
  const { leagueId } = useParams();
  const navigate = useNavigate();

  const [leagueImage, setLeagueImage] = useState('');
  const [imageLoadFailed, setImageLoadFailed] = useState(false);
  const [eloMin, setEloMin] = useState(null);
  const [eloMax, setEloMax] = useState(null);
  const [price, setPrice] = useState(0);
  const [participantsCount, setParticipantsCount] = useState(0);
  const [participantsLimit, setParticipantsLimit] = useState(null);
  const [limitReached, setLimitReached] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [duplicateStatus, setDuplicateStatus] = useState({
    checking: false,
    exists: false,
    reason: null,
    message: '',
  });

  const [formData, setFormData] = useState({
    teamName: '',
    email: '',
    newsletter: false,
    consentInfo: false,

    reglement: false,
    joueur1: { nom: '', prenom: '', phone: '', elo: '', isLocked: false },
    joueur2: { nom: '', prenom: '', phone: '', elo: '', isLocked: false },
  });

  const isValidPhone = (phone) => /^04\d{8}$/.test(phone.trim());

  const handleChange = (e, key, subkey = null) => {
    const { name, value, type, checked } = e.target;
    const val = type === 'checkbox' ? checked : value;

    if (key && subkey) {
      setFormData((prev) => {
        const updated = { ...prev, [key]: { ...prev[key], [subkey]: val } };
        if (subkey === 'phone' && !isValidPhone(val.trim())) updated[key].isLocked = false;
        return updated;
      });
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: val }));
  };

  const fillPlayerFromPhone = async (playerKey) => {
    const phone = formData[playerKey].phone.trim();
    if (!isValidPhone(phone)) return;

    try {
      const res = await fetch(functionUrl('getJoueurByPhone', { phone }));
      const data = await res.json();

      if (data) {
        setFormData((prev) => ({
          ...prev,
          [playerKey]: {
            ...prev[playerKey],
            nom: data.nom,
            prenom: data.prenom,
            elo: data.elo,
            isLocked: true,
          },
        }));
      } else {
        setFormData((prev) => ({
          ...prev,
          [playerKey]: { ...prev[playerKey], elo: '700', isLocked: false },
        }));
      }
    } catch (error) {
      console.error(`Erreur ${playerKey} :`, error);
    }
  };

  useEffect(() => {
    fillPlayerFromPhone('joueur1');
  }, [formData.joueur1.phone]);

  useEffect(() => {
    fillPlayerFromPhone('joueur2');
  }, [formData.joueur2.phone]);

  useEffect(() => {
    const fetchLeagueDetails = async () => {
      try {
        const res = await fetch(functionUrl('protectedGetLeagueRegistrationPublicDetails', { leagueId }));
        if (!res.ok) throw new Error(`Erreur function ligue: ${res.status}`);
        const data = await res.json();

        setPrice(data.price || 0);
        setLeagueImage(data.registrationPicture || '');
        setImageLoadFailed(false);
        setEloMin(data.eloMin !== undefined ? Number(data.eloMin) : null);
        setEloMax(data.eloMax !== undefined ? Number(data.eloMax) : null);
        setParticipantsLimit(Number(data.participantsLimit || data.players || 12));
        setParticipantsCount(Number(data.participantsCount || 0));
        setLimitReached(Boolean(data.limitReached));
      } catch (error) {
        console.error('Erreur lors de la recuperation des details de la ligue :', error);
        setLeagueImage('');
        setImageLoadFailed(false);
      }
    };

    fetchLeagueDetails();
  }, [leagueId]);

  useEffect(() => {
    const teamName = formData.teamName.trim();
    const email = formData.email.trim();

    if (!teamName && !email) {
      setDuplicateStatus({ checking: false, exists: false, reason: null, message: '' });
      return;
    }

    const shouldCheckTeamName = teamName.length >= 2;
    const shouldCheckEmail = email.includes('@') && email.includes('.');

    if (!shouldCheckTeamName && !shouldCheckEmail) {
      setDuplicateStatus((prev) => ({ ...prev, checking: false, exists: false, reason: null, message: '' }));
      return;
    }

    let cancelled = false;
    const timeoutId = window.setTimeout(async () => {
      setDuplicateStatus((prev) => ({ ...prev, checking: true }));

      try {
        const duplicateData = await postFunctionJson('protectedCheckLeagueRegistrationDuplicate', {
          leagueId,
          teamName: shouldCheckTeamName ? teamName : '',
          email: shouldCheckEmail ? email : '',
        });

        if (cancelled) return;
        setDuplicateStatus({
          checking: false,
          exists: Boolean(duplicateData.exists),
          reason: duplicateData.reason || null,
          message: duplicateData.exists ? duplicateMessage(duplicateData.reason, t) : '',
        });
      } catch (error) {
        if (cancelled) return;
        console.error('Erreur verification doublon ligue :', error);
        setDuplicateStatus({ checking: false, exists: false, reason: null, message: '' });
      }
    }, 450);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [formData.teamName, formData.email, leagueId]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.consentInfo) {                                                                                                                                                                                              
      alert(t('consentAlert'));
      return;                                                                                                                                                                                                                 
    }   
    if (!formData.reglement) return alert(t('rulesAlert'));
    if (!isValidPhone(formData.joueur1.phone) || !isValidPhone(formData.joueur2.phone)) return alert(t('phoneAlert'));
    if (formData.joueur1.phone.trim() === formData.joueur2.phone.trim()) return alert(t('samePhone'));

    const sameFirstName = formData.joueur1.prenom.trim().toLowerCase() === formData.joueur2.prenom.trim().toLowerCase();
    const sameLastName = formData.joueur1.nom.trim().toLowerCase() === formData.joueur2.nom.trim().toLowerCase();
    if (sameFirstName && sameLastName) return alert(t('samePlayer'));

    const totalElo = parseInt(formData.joueur1.elo || 0, 10) + parseInt(formData.joueur2.elo || 0, 10);
    if ((eloMin && totalElo < eloMin) || (eloMax && totalElo > eloMax)) {
      alert(t('eloAlert', { total: totalElo, min: eloMin, max: eloMax }));
      return;
    }

    try {
      if (duplicateStatus.exists) return alert(duplicateStatus.message || t('duplicateFallback'));

      const duplicateData = await postFunctionJson('protectedCheckLeagueRegistrationDuplicate', {
        leagueId,
        teamName: formData.teamName.trim(),
        email: formData.email.trim(),
      });

      if (duplicateData.exists) {
        const message = duplicateMessage(duplicateData.reason, t);
        setDuplicateStatus({ checking: false, exists: true, reason: duplicateData.reason || null, message });
        alert(message);
        return;
      }

      if (participantsLimit && participantsCount >= participantsLimit) {
        setLimitReached(true);
        return alert(t('maxReached'));
      }

      const data = await postFunctionJson('protectedCreateLeagueCheckoutPublicV2', {
        leagueId,
        teamName: formData.teamName,
        email: formData.email,
        joueur1: formData.joueur1,
        joueur2: formData.joueur2,
        price,
        language,
        newsletter: formData.newsletter,
        consentInfo: formData.consentInfo, 
      });

      if (data.url) window.location.href = data.url;
      else throw new Error('Aucune URL de session trouvee');
    } catch (error) {
      console.error('Erreur lors de la creation de la session Stripe :', error);
      alert(t('genericError'));
    }
  };

  const closeModal = () => {
    setShowModal(false);
    navigate('/');
  };

  return (
    <section className="container mx-auto my-16 px-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-stretch h-full min-h-[700px]">
        <div className="flex justify-center items-center">
          <div className="relative w-full h-full min-h-[700px] overflow-hidden rounded-lg shadow-lg">
            {leagueImage && !imageLoadFailed ? (
              <img
                src={leagueImage}
                alt={`Ligue de Padel ${leagueId}`}
                className="object-contain w-full h-full transition-transform duration-300 hover:scale-105"
                onError={() => {
                  console.error('Erreur chargement image ligue:', leagueImage);
                  setImageLoadFailed(true);
                }}
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-gray-400">{t('imageMissing')}</div>
            )}
          </div>
        </div>

        <div className="p-6 bg-white shadow-lg rounded-r-lg transition-transform duration-300 hover:scale-105">
          <h2 className="text-2xl font-semibold text-[#4c2f8e] mb-4">{t('registrationTitle')}</h2>
          {limitReached ? (
            <p className="text-red-500">{t('maxReached')}</p>
          ) : (
            <form className="space-y-4" onSubmit={handleSubmit}>
              <div className="flex flex-col">
                <label htmlFor="teamName" className="text-lg font-medium text-gray-700">{t('teamName')}</label>
                <input id="teamName" name="teamName" type="text" className="p-2 border rounded-md focus:outline-none focus:ring focus:border-[#4c2f8e]" placeholder={t('teamPlaceholder')} value={formData.teamName} onChange={handleChange} required />
                {duplicateStatus.reason === 'teamName' && duplicateStatus.message && <p className="mt-1 text-sm text-red-600">{duplicateStatus.message}</p>}
              </div>

              <div className="flex flex-col">
                <label htmlFor="email" className="text-lg font-medium text-gray-700">{t('email')}</label>
                <input id="email" name="email" type="email" className="p-2 border rounded-md focus:outline-none focus:ring focus:border-[#4c2f8e]" placeholder={t('emailPlaceholder')} value={formData.email} onChange={handleChange} required />
                {duplicateStatus.reason === 'email' && duplicateStatus.message && <p className="mt-1 text-sm text-red-600">{duplicateStatus.message}</p>}
              </div>

              {['joueur1', 'joueur2'].map((key, index) => (
                <div key={key} className="p-4 border border-gray-300 rounded-md bg-gray-50">
                  <h3 className="text-md font-semibold text-[#4c2f8e] mb-2">{t('player', { number: index + 1 })}</h3>
                  <div className="flex flex-col mb-4">
                    <label className="text-sm text-gray-700 flex items-center gap-2 mb-1">
                      {t('phone')}
                      <div className="flex items-center text-blue-600 text-xs bg-blue-50 px-2 py-1 rounded-full"><span>{t('phoneHint')}</span></div>
                    </label>
                    <input type="tel" className="p-2 border border-blue-400 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-300 focus:border-blue-500" placeholder="0470707070" value={formData[key].phone} onChange={(event) => handleChange(event, key, 'phone')} required />
                    <div className="mt-2 text-sm text-gray-600 bg-gray-50 border-l-4 border-blue-400 p-3 rounded-md">{t('knownPlayer')}<br />{t('newPlayer')}</div>
                  </div>

                  <div className="flex flex-col mb-2">
                    <label className="text-sm text-gray-700">{t('lastName')}</label>
                    <input type="text" className="p-2 border rounded-md focus:outline-none focus:ring focus:border-[#4c2f8e]" placeholder={t('lastName')} value={formData[key].nom} onChange={(event) => handleChange(event, key, 'nom')} required />
                  </div>

                  <div className="flex flex-col mb-2">
                    <label className="text-sm text-gray-700">{t('firstName')}</label>
                    <input type="text" className="p-2 border rounded-md focus:outline-none focus:ring focus:border-[#4c2f8e]" placeholder={t('firstName')} value={formData[key].prenom} onChange={(event) => handleChange(event, key, 'prenom')} required />
                  </div>

                  {formData[key].isLocked ? (
                    <div className="flex flex-col">
                      <label className="text-sm text-gray-700">{t('elo')}</label>
                      <input type="number" className="p-2 border rounded-md focus:outline-none focus:ring focus:border-[#4c2f8e]" value={formData[key].elo} disabled />
                    </div>
                  ) : (
                    <div className="flex flex-col mb-2">
                      <label className="text-sm text-gray-700">{t('estimatedLevel')}</label>
                      <select className="p-2 border rounded-md focus:outline-none focus:ring focus:border-[#4c2f8e]" value={formData[key].elo} onChange={(event) => handleChange({ target: { value: parseInt(event.target.value, 10), type: 'select-one' } }, key, 'elo')} required>
                        <option value="">{t('chooseCategory')}</option>
                        {Object.entries(eloCategories).map(([label, value]) => <option key={label} value={value}>{label} - {value} {t('points')}</option>)}
                      </select>
                      <div className="mt-1 text-xs text-gray-500 italic">{t('eloHint')}</div>
                    </div>
                  )}
                </div>
              ))}

              <div className="flex items-center">
              <div className="flex items-center">                                                                                                                                                                                       
    <input                                                                                                                                                                                                                  
      type="checkbox"                                                                                                                                                                                                       
      id="consentInfo"                                                                                                                                                                                                      
      name="consentInfo"                                                                                                                                                                                                    
      checked={formData.consentInfo}                                                                                                                                                                                        
      onChange={handleChange}                                                                                                                                                                                               
      className="h-4 w-4 text-[#4c2f8e] focus:ring-[#4c2f8e] border-gray-300 rounded"                                                                                                                                       
      required                                                                                                                                                                                                              
    />                                                                                                                                                                                                                      
    <label htmlFor="consentInfo" className="ml-2 text-gray-700">                                                                                                                                                            
      {t('consent')}
    </label>                                                                                                                                                                                                                
  </div>                                                                                                                                                                                                                    
                                                                                                                                                                                                                            
                                                                                                                                                                                                               
                                                                                                                                                                                                                            
                                                                                                                                                                                                              
              </div>

              <div className="flex items-center">
                <input type="checkbox" id="reglement" name="reglement" className="h-4 w-4 text-[#4c2f8e] focus:ring-[#4c2f8e] border-gray-300 rounded" checked={formData.reglement} onChange={handleChange} required />
                <label htmlFor="reglement" className="ml-2 text-gray-700">{t('acceptRules')}</label>
              </div>

              <button type="submit" disabled={duplicateStatus.checking || duplicateStatus.exists} className={`mt-6 w-full px-6 py-3 bg-gradient-to-r from-[#4c2f8e] to-[#ea4c89] text-white rounded-full shadow-lg transform transition-all duration-300 ${duplicateStatus.checking || duplicateStatus.exists ? 'opacity-60 cursor-not-allowed' : 'hover:scale-110 hover:shadow-2xl'}`}>
                {duplicateStatus.checking ? t('checking') : t('register')}
              </button>
            </form>
          )}
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white p-8 rounded-lg shadow-lg w-11/12 md:w-1/2 lg:w-1/3">
            <h3 className="text-xl font-bold mb-4 text-center">{t('confirmed')}</h3>
            <div className="flex flex-col md:flex-row items-center">
              <div className="md:w-1/2"><p className="mb-4"><br />{t('finalPayment')}<br /><br /><strong>{t('payment')} :</strong> BE45001739005589<br /><strong>{t('price')} : </strong> {t('perPlayer')}<br /><strong>{t('paymentReference')} :</strong> {t('teamName')}<br /><br />{t('viewPools')}<br /><br /><strong className="text-center">{t('ready')}</strong></p></div>
              <div className="md:w-1/2 flex flex-col items-center mt-4 md:mt-0"><p className="text-center mb-2 text-gray-700">{t('scan')}</p><QRCodeCanvas value={`BCD
001
1
SCT
BICDEABCXXX
Mendyx ASBL
BE45001739005589
EUR40.00`} size={150} level="M" /></div>
            </div>
            <button onClick={closeModal} className="mt-4 w-full px-4 py-2 bg-gradient-to-r from-[#4c2f8e] to-[#ea4c89] text-white rounded-full">{t('close')}</button>
          </div>
        </div>
      )}
    </section>
  );
};

export default LeagueRegistration;
